import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AppInfoService } from 'core/app-info/app-info.service';
import { AppStateService } from 'core/app-state/app-state.service';
import { NotificationType } from 'core/notification/notification-config.class';
import { NotificationService } from 'core/notification/notification.service';
import { AppLocalization } from 'src/app/modules/shared/entities/app-localization';
import { BaseResponse } from 'src/app/modules/shared/entities/base-response';
import { QuoteVersion } from '../entities/quoteVersion.entity';
import { QuoteRestoreComponent } from '../quote-restore/quote-restore.component';
import { PrintExportQuoteComponent } from '../quote/print-export-quote/print-export-quote.component';
import { QuoteDetailsService } from '../services/quote-details.service';
import { QuoteToolsService } from '../services/quote-tools.service';
import { ShortContact } from '../entities/short-contact.class';

@Component({
  selector: 'app-quote-versioning',
  templateUrl: './quote-versioning.component.html',
  styleUrls: ['./quote-versioning.component.scss'],
  standalone: false
})
export class QuoteVersioningComponent implements OnInit, OnDestroy {

  appStrings: AppLocalization;
  quoteVersionList: QuoteVersion[];
  quoteId: string;
  quoteRestoreDialogWidth = '480px';
  quoteEmailDialogWidth = '370px';
  quoteToBeRestored;
  isCompleted = false;
  currencySymbol: any;
  isLoading = false;
  subs = new Subscription();
  @Output() saveQuote = new EventEmitter();
  @Input() ensEmails: string[] = [];
  @Input() contacts: ShortContact[] = [];

  displayedColumns = [
    'quoteRevision',
    'quoteCreatedDate',
    'quoteAmountTotal',
    'quoteAge',
    'quoteActions',
  ];

  constructor(
    private appInfoService: AppInfoService,
    private quoteDetailsService: QuoteDetailsService,
    private quoteToolsService: QuoteToolsService,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private router: Router,
    private notificationService: NotificationService,
    private appStateService: AppStateService
  ) { }

  ngOnInit() {
    this.appStrings = this.appInfoService.appStrings;
    this.setQuoteId();
    this.initData();
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  private setQuoteId() {
    const newQuoteId = this.route.snapshot.paramMap.get('quoteId');
    if (newQuoteId) {
      this.quoteId = newQuoteId;
    }
  }

  initData() {
    this.quoteVersionList = [];
    this.getQuoteRevisions();
    this.subs.add(this.route.paramMap.subscribe(params => {
      const newQuoteId = params.get('quoteId');
      if (newQuoteId && newQuoteId !== this.quoteId) {
        this.quoteId = newQuoteId;
        this.getQuoteRevisions();
      }
    }));
    this.subs.add(this.quoteDetailsService.isCompleted$.subscribe(isCompleted => {
      this.isCompleted = isCompleted;
    }));
    this.subs.add(this.quoteDetailsService.currencySign$.subscribe(currencySign =>
      this.currencySymbol = currencySign
    ));
    this.subs.add(this.quoteToolsService.exportLoading$.subscribe(inProgress => {
      this.isLoading = inProgress;
    }));
  }

  /**
   * Gets quote revisions
   */
  getQuoteRevisions() {
    this.setQuoteId();
    if (this.quoteId) {
      this.subs.add(this.quoteDetailsService.getQuoteRevisions(this.quoteId)
        .subscribe((result: QuoteVersion[]) => {
          this.quoteVersionList = result;
          this.quoteVersionList.forEach(quote => quote.age = this.formatQuoteAge(quote.age as unknown as number));
        }));
    }
  }

  /**
   * Formats the quote age to display the age in days
   * @param age - the age in days as returned by the API
   * @returns - formatted age string
   */
  private formatQuoteAge(age: number): string {
    if (age === 0) {
      return this.appStrings.label_today;
    } else {
      if (age === 1) {
        return `${age} ${this.appStrings.label_day}`;
      }
      return `${age} ${this.appStrings.label_days}`;
    }
  }

  /**
   * Triggers the opening of PrintExportQuoteComponent dialog
   * @param quote - the quote to be sent via email
   */
  emailQuote(quote: QuoteVersion) {
    const dialogRef = this.dialog.open(PrintExportQuoteComponent, {
      width: this.quoteEmailDialogWidth,
      data: { quote, isEmailOnly: true, ensEmails: this.ensEmails, contacts: this.contacts }
    });

    this.subs.add(dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.callbackEmailQuote(result, quote);
      }
    }));
  }

  /**
   * Print the selected quote
   * @param quote - the quote to be printed
   */
  printQuote(quote) {
    this.quoteToolsService.printQuote(quote, true);
  }

  /**
   * Export the selected quote
   * @param quote - the quote to be exported
   */
  exportQuote(quote) {
    quote.quoteNumber = quote.quoteRevisionNumber;
    this.quoteToolsService.exportQuote(quote, true);
  }

  restoreQuote(quote) {
    const dialogRef = this.dialog.open(QuoteRestoreComponent, {
      width: this.quoteRestoreDialogWidth,
      data: {
        quote
      }
    });

    this.subs.add(dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.quoteToBeRestored = quote;
        if (result.discardCurrentAndRestoreSelected) {
          this.discardCurrentAndRestoreSelectedQuote();
        } else if (result.restoreSelectedAndSaveCurrent) {
          this.restoreSelectedAndSaveCurrent();
        }
      }
    }));
  }

  private callbackEmailQuote(action, quote) {
    this.subs.add(this.quoteToolsService.emailQuote(quote.quoteRevisionId, action.emailAddresses, true)
      .subscribe((result: BaseResponse) => this.notificationService.showNotification(result.actionMessage, NotificationType.Success)));
  }

  /**
   * Discard the selected quote and save current quote as new revision
   */
  private discardCurrentAndRestoreSelectedQuote() {
    this.restoreSelected();
  }

  /**
   * Restore selected quote and save current quote as new revision
   */
  private restoreSelectedAndSaveCurrent() {
    this.saveQuote.emit(() => this.quoteDetailsService.completeQuote(this.quoteId).subscribe(() => this.restoreSelected()));
  }

  /**
   * Restores the selected quote
   */
  private restoreSelected() {
    this.isLoading = true;
    this.quoteDetailsService.restoreQuoteRevision(this.quoteToBeRestored.quoteRevisionId)
      .subscribe(() => {
        this.isLoading = false;
        if (window.location.pathname.includes('new')) {
          this.router.navigate(['/quotes', this.quoteId]);
        } else {
          if (this.appStateService.state.isElectron) {
            this.quoteToolsService.productListHasChanges$.next(false);
            this.router.navigate(['/quotes', this.quoteId, { nonce: Math.random() }]);
            return;
          }
          window.location.reload();
        }
      });
  }
}
